import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Bell } from "lucide-react";
import {
  Activity, Shield, Users, FileText, Inbox, CheckCircle2, XCircle, Zap, Bot,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, BarChart, Bar,
} from "recharts";

const Stat = ({ label, value, icon: Icon, tone = "default", testid }) => {
  const toneMap = {
    default: "text-white",
    ok: "text-[var(--good)]",
    warn: "text-[var(--warn)]",
    danger: "text-[var(--danger)]",
    accent: "text-[var(--accent)]",
  };
  return (
    <div
      className="border border-[var(--border)] bg-[var(--surface)] p-5 hover-neon relative"
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="label-mono">{label}</div>
        <Icon className="w-4 h-4 text-[var(--text-dim)]" strokeWidth={2} />
      </div>
      <div className={`font-display font-black text-4xl tracking-tight ${toneMap[tone]}`}>
        {value}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attention, setAttention] = useState([]);

  const load = () => {
    api.get("/metrics/overview")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
    api.get("/attention").then((r) => setAttention(r.data.items || [])).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="p-8 text-[var(--text-dim)]" data-testid="dashboard-loading">
        Cargando panel de mando…
      </div>
    );
  }

  const t = data?.totals || {};
  const timeline = data?.timeline?.length
    ? data.timeline
    : [{ date: new Date().toISOString().slice(0, 10), actions: 0 }];

  const perAgent = (data?.per_agent || []).map((p) => ({ ...p, name: p.agent_id.toUpperCase() }));

  return (
    <div className="p-8 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[var(--border)] pb-5">
        <div>
          <div className="label-mono mb-2">COMMAND CENTER · {user?.company_name}</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Panel de Mando</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Estado en vivo de los agentes, seguridad, aprobaciones y auditoría.
          </p>
        </div>
        <div className="text-right">
          <div className="label-mono">Marco Operativo</div>
          <div className="font-mono text-xs text-white mt-1">OWASP · NIST · ISO 42001</div>
        </div>
      </div>

      {/* Requiere tu atención (proactividad) */}
      {attention.length > 0 && (
        <div className="border border-[var(--accent)] bg-[var(--accent)]/5 p-4" data-testid="attention-panel">
          <div className="flex items-center gap-2 mb-3">
            <Bell className="w-4 h-4" style={{ color: "var(--accent)" }} />
            <div className="label-mono" style={{ color: "var(--accent)" }}>REQUIERE TU ATENCIÓN</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {attention.map((it) => (
              <button key={it.kind} onClick={() => nav(it.link)}
                      className="flex items-center gap-2 px-3 py-2 border border-[var(--border)] bg-[var(--surface)] text-sm text-white hover:border-[var(--accent)]"
                      data-testid={`att-${it.kind}`}>
                <span className="font-display font-bold" style={{ color: "var(--accent)" }}>{it.count}</span>
                {it.text}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* KPI Row */}
      <div className="grid grid-cols-4 gap-4">
        <Stat label="Agentes Activos" value={`${t.active_agents}/${t.total_agents}`}
              icon={Bot} tone="accent" testid="stat-agents" />
        <Stat label="Aprobaciones Pendientes" value={t.pending_approvals}
              icon={Inbox} tone={t.pending_approvals > 0 ? "warn" : "default"} testid="stat-pending" />
        <Stat label="Eventos Auditados" value={t.audit_events} icon={Activity} testid="stat-audit" />
        <Stat label="Docs en Conocimiento" value={t.knowledge_docs} icon={FileText} testid="stat-docs" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 border border-[var(--border)] bg-[var(--surface)] p-5" data-testid="chart-timeline">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-mono mb-1">ACTIVIDAD · ÚLTIMOS 14 DÍAS</div>
              <div className="font-display font-bold text-lg">Acciones auditadas por día</div>
            </div>
            <Zap className="w-4 h-4 text-[var(--accent)]" />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={timeline}>
              <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94A3B8", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "#131722", border: "1px solid #272D3D", borderRadius: 0, fontFamily: "JetBrains Mono" }}
                labelStyle={{ color: "#94A3B8" }}
              />
              <Line type="monotone" dataKey="actions" stroke="#00E5FF" strokeWidth={2} dot={{ fill: "#00E5FF", r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid="chart-approvals">
          <div className="label-mono mb-4">APROBACIONES</div>
          <div className="space-y-4">
            <Row icon={<CheckCircle2 className="w-4 h-4 text-[var(--good)]" />} label="Aprobadas" value={t.approved} />
            <Row icon={<XCircle className="w-4 h-4 text-[var(--danger)]" />} label="Rechazadas" value={t.rejected} />
            <Row icon={<Inbox className="w-4 h-4 text-[var(--warn)]" />} label="Pendientes" value={t.pending_approvals} />
            <Row icon={<Users className="w-4 h-4 text-[var(--accent)]" />} label="Sesiones Chat" value={t.chat_sessions} />
          </div>
        </div>
      </div>

      {/* Per-agent bar */}
      <div className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid="chart-agents">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mono mb-1">CARGA POR AGENTE</div>
            <div className="font-display font-bold text-lg">Eventos auditados por agente</div>
          </div>
          <Shield className="w-4 h-4 text-[var(--text-dim)]" />
        </div>
        {perAgent.length === 0 ? (
          <div className="text-sm text-[var(--text-dim)] font-mono py-10 text-center">
            Aún no hay actividad. Chatea con un agente para poblar este panel.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={perAgent}>
              <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <YAxis tick={{ fill: "#94A3B8", fontFamily: "JetBrains Mono", fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: "#131722", border: "1px solid #272D3D", borderRadius: 0, fontFamily: "JetBrains Mono" }}
                labelStyle={{ color: "#94A3B8" }}
              />
              <Bar dataKey="count" fill="#00E5FF" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

const Row = ({ icon, label, value }) => (
  <div className="flex items-center justify-between border-b border-[var(--border)] pb-3 last:border-0 last:pb-0">
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-sm">{label}</span>
    </div>
    <span className="font-mono text-lg font-bold">{value}</span>
  </div>
);
